import React from "react";
import withStyles from "@material-ui/styles/withStyles";
import Loader from "storybookComponents/Loader";
import Button from "storybookComponents/Button";
import SimpleSelect from "storybookComponents/dropDown/SimpleSelect";
import Modal from "components/Modal";
import { getPropertyId } from "common";
import UploadBox from "./UploadBox";
import BrokerList from "./BrokerList";
import Arrow from "../components/Arrow";
import CheckBox from "../components/CheckBox";
import { uploadCSV, getImportLogs, getContactGroups, importCSV, archiveCSV } from "../apis";
import { getColumns } from "../utils";
import styles from "../styles";
import { getErrorMessage, checkUncatchableError } from "environments/uncatchableErrorStore";

const BrokerUpload = ({ classes, showNotification, event }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [logsData, setLogsData] = React.useState([]);
  const [broker, setBroker] = React.useState(null);
  const [openModal, setOpenModal] = React.useState(false);
  const [CSVColumns, setCSVColumns] = React.useState([]);
  const [compactView, setCompactView] = React.useState(false);
  const [tempFile, setTempFile] = React.useState(null);

  const handleUpload = (base64, fileName) => {
    let csvData = base64.toString().replace(/^data:(.*,)?/, "");
    let inputData = {
      propertyId: getPropertyId(),
      name: fileName,
      csvData,
    };

    uploadCSV(inputData)
      .then((data) => {
        const columns = getColumns(data.columnNames);
        setCSVColumns(columns);
        setCompactView(columns.fullNameColumn ? true : false);
        setTempFile(data.tempfile);
        setOpenModal(true);
      })
      .catch((err) => {
        const uncachebleError = getErrorMessage(checkUncatchableError("uploadCsvMutation"));
        showNotification(uncachebleError || "Something went wrong!", "error");
      });
  };

  const handleCloseModal = () => {
    setTempFile(null);
    setCompactView(false);
    setCSVColumns([]);
    setOpenModal(false);
  };

  const handleImportCSV = () => {
    let inputData = {
      propertyId: getPropertyId(),
      tempfile: tempFile,
      emailColumn: CSVColumns.emailColumn,
    };
    setOpenModal(false);

    if (compactView) inputData = { ...inputData, fullNameColumn: CSVColumns.fullNameColumn };
    else
      inputData = {
        ...inputData,
        firstNameColumn: CSVColumns.firstNameColumn,
        lastNameColumn: CSVColumns.lastNameColumn,
      };

    setIsLoading(true);
    importCSV(inputData)
      .then(() => {
        showNotification("You have successfully imported your csv file.", "success");
        setTempFile(null);
        setCompactView(false);
        setCSVColumns([]);
        getData();
        showNotification("Please wait few minutes for the contacts to be uploaded.", "warning");
      })
      .catch(() => {
        const uncachebleError = getErrorMessage(checkUncatchableError("importCsvMutation"));
        showNotification(uncachebleError || "Something went wrong!", "error");
      });
  };

  const handleArchive = (logId) => {
    setIsLoading(true);
    archiveCSV(logId)
      .then(() => {
        getData();
        showNotification("You have successfully archived the file.", "success");
      })
      .catch(() => {
        const uncachebleError = getErrorMessage(checkUncatchableError("undoImportMutation"));
        showNotification(uncachebleError || "Something went wrong!", "error");
      });
  };

  const handleColumns = (e) => {
    let updatedcolumns = {
      columns: CSVColumns.columns,
      emailColumn: CSVColumns.emailColumn,
      firstNameColumn: CSVColumns.firstNameColumn,
      fullNameColumn: CSVColumns.fullNameColumn,
      lastNameColumn: CSVColumns.lastNameColumn,
    };
    setCSVColumns({ ...updatedcolumns, [e.target.name]: e.target.value });
  };

  const getData = () => {
    setIsLoading(true);
    Promise.all([getImportLogs(), getContactGroups()])
      .then((values) => {
        setLogsData(values[0]);
        setBroker(values[1]);
        setIsLoading(false);
      })
      .catch((err) => {
        showNotification(err.message || "Something went wrong!", "error");
        setIsLoading(false);
      });
  };

  const renderModalActions = () => (
    <>
      <Button color="darkBlack" variant="tertiary" size="md" onClick={handleCloseModal}>
        Cancel
      </Button>
      <Button color="darkBlack" type="submit" variant="primary" size="md" onClick={handleImportCSV}>
        Import CSV
      </Button>
    </>
  );

  const renderModalContent = () => (
    <>
      <p>Match the data on the left with the appropriate column in your file</p>
      <div className={classes.fieldBox}>
        <p className={classes.fieldLabel}>Email</p>
        <Arrow />
        <div className={classes.fieldSelectBox}>
          <SimpleSelect
            topLabel=""
            bottomLabel=""
            name="emailColumn"
            values={CSVColumns.columns}
            selected_value={CSVColumns.emailColumn}
            handleChange={handleColumns}
          />
        </div>
      </div>
      {CSVColumns.fullNameColumn && CSVColumns.firstNameColumn && CSVColumns.lastNameColumn && (
        <div className={classes.fieldCheckBox} style={{ marginTop: 20 }}>
          <p className={classes.fieldLabel}>First Name/Last Name</p>

          <div className={classes.fieldCheckBoxWrapper}>
            <CheckBox
              compactView={compactView}
              handleCompactView={() => setCompactView(!compactView)}
            />
          </div>
        </div>
      )}
      {compactView && CSVColumns.fullNameColumn ? (
        <div className={classes.fieldBox}>
          <p className={classes.fieldLabel}>Full Name</p>
          <Arrow />
          <div className={classes.fieldSelectBox}>
            <SimpleSelect
              topLabel=""
              bottomLabel=""
              name="fullNameColumn"
              values={CSVColumns.columns}
              selected_value={CSVColumns.fullNameColumn}
              handleChange={handleColumns}
            />
          </div>
        </div>
      ) : !compactView && CSVColumns.firstNameColumn && CSVColumns.lastNameColumn ? (
        <>
          <div className={classes.fieldBox}>
            <p className={classes.fieldLabel}>First Name</p>
            <Arrow />
            <div className={classes.fieldSelectBox}>
              <SimpleSelect
                topLabel=""
                bottomLabel=""
                name="firstNameColumn"
                values={CSVColumns.columns}
                selected_value={CSVColumns.firstNameColumn}
                handleChange={handleColumns}
              />
            </div>
          </div>

          <div className={classes.fieldBox}>
            <p className={classes.fieldLabel}>Last Name</p>
            <Arrow />
            <div className={classes.fieldSelectBox}>
              <SimpleSelect
                topLabel=""
                bottomLabel=""
                name="lastNameColumn"
                values={CSVColumns.columns}
                selected_value={CSVColumns.lastNameColumn}
                handleChange={handleColumns}
              />
            </div>
          </div>
        </>
      ) : null}
    </>
  );

  // Init actions
  React.useEffect(() => {
    getData();
  }, []);

  return (
    <div className={classes.contentBox}>
      {isLoading && <Loader />}
      {!isLoading && (
        <>
          <UploadBox
            classes={classes}
            onUpload={handleUpload}
            disabled={!broker}
            showNotification={showNotification}
          />
          <BrokerList classes={classes} data={logsData} onArchive={handleArchive} event={event} />
          <Modal
            title="Import CSV"
            open={openModal}
            handleClose={handleCloseModal}
            actionChildren={renderModalActions()}
          >
            {renderModalContent()}
          </Modal>
        </>
      )}
    </div>
  );
};

export default withStyles(styles)(BrokerUpload);
